import {
  ActionType, EntityAccessLevel, EntityLabel, type EntityPermissions,
  type RestrictedFields,
} from '@app/data/settings/permissionTemplates';
import { type PermissionTemplateType } from '@app/services/permissionTemplates/permissionTemplates';
import { type EntityRestrictionFilter, filterRestrictedFields } from '@app/utils/permissionFilters';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export enum UserListType {
  ALL_USERS = 'all_users',
  SALES_GROUP_USERS = 'group_users',
}

export interface PermissionTemplateForm {
  template_name: string;
  description?: string;
  userList: UserListType;
  customEntities: {
    workflow: boolean;
    report: boolean;
  }
  entityPermissions: EntityPermissions;
}

export const defaultEntityActionPermissions = {
  [ActionType.VIEW]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.CREATE]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.EDIT]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.DELETE]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.EXPORT]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.IMPORT]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
  [ActionType.MARK_COMPLETE]: { access: EntityAccessLevel.NO_ACCESS, properties: null },
} as const;

export const initialEntityPermissions: EntityPermissions = {
  [EntityLabel.LEADS]: defaultEntityActionPermissions,
  [EntityLabel.ACTIVITIES]: defaultEntityActionPermissions,
  [EntityLabel.TASKS]: defaultEntityActionPermissions,
  [EntityLabel.DEALS]: defaultEntityActionPermissions,
};

const initialState: PermissionTemplateForm = {
  template_name: '',
  description: '',
  userList: UserListType.SALES_GROUP_USERS,
  customEntities: {
    workflow: false,
    report: false,
  },
  entityPermissions: initialEntityPermissions,
};

const isValidEntityLabel = (entity: string):
  entity is EntityLabel => Object.values(EntityLabel).includes(entity as EntityLabel);

const PermissionTemplateFormSlice = createSlice({
  name: 'permissionTemplateForm',
  initialState,
  reducers: {
    setInitialData: (
      state,
      action: PayloadAction<{
        name: string;
        description: string;
        userList: UserListType;
        workflow: boolean;
        report: boolean;
      }>,
    ) => {
      const {
        name, description, userList, workflow, report,
      } = action.payload;
      state.template_name = name;
      state.description = description;
      state.userList = userList;
      state.customEntities.workflow = workflow;
      state.customEntities.report = report;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.template_name = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setUserList: (state, action: PayloadAction<UserListType>) => {
      state.userList = action.payload;
    },
    setWorkFlowAccess: (state, action: PayloadAction<boolean>) => {
      state.customEntities.workflow = action.payload;
    },
    setReportAccess: (state, action: PayloadAction<boolean>) => {
      state.customEntities.report = action.payload;
    },
    updateEntityPermission: (
      state,
      action: PayloadAction<{
        entity: EntityLabel;
        actionType: ActionType;
        permission: { access: EntityAccessLevel; properties: RestrictedFields | null };
      }>,
    ) => {
      const { entity, actionType, permission } = action.payload;
      if (state.entityPermissions[entity]) {
        state.entityPermissions[entity][actionType] = permission;
      }
    },
    updateEntityPermissionsBulk: (
      state,
      action: PayloadAction<{
        entityPermissions: PermissionTemplateType['entity_permissions'];
        filters?: EntityRestrictionFilter;
      }>,
    ) => {
      const { entityPermissions, filters } = action.payload;
      entityPermissions.forEach((entity) => {
        if (isValidEntityLabel(entity.entity)) {
          const entityLabel = entity.entity as EntityLabel;

          entity.permissions.forEach((perm) => {
            const actionType = perm.action as ActionType;
            const filteredRestrictedFields = filterRestrictedFields(
              entityLabel,
              perm.properties?.restricted_fields,
              filters,
            );

            const permission = {
              access: perm.access as EntityAccessLevel,
              properties: filteredRestrictedFields?.length
                ? { restricted_fields: filteredRestrictedFields }
                : null,
            };

            if (state.entityPermissions[entityLabel]) {
              state.entityPermissions[entityLabel][actionType] = permission;
            }
          });
        }
      });
    },
    resetForm: (state) => {
      state.template_name = initialState.template_name;
      state.description = initialState.description;
      state.userList = initialState.userList;
      state.customEntities.workflow = initialState.customEntities.workflow;
      state.customEntities.report = initialState.customEntities.report;
      state.entityPermissions = initialEntityPermissions;
    },
  },
});

export const {
  setInitialData,
  setName,
  setDescription,
  setUserList,
  setWorkFlowAccess,
  setReportAccess,
  updateEntityPermission,
  updateEntityPermissionsBulk,
  resetForm,
} = PermissionTemplateFormSlice.actions;

export default PermissionTemplateFormSlice.reducer;
